import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { useLocation, useNavigate } from 'react-router-dom'
import { GlobalContext, UserContext } from '../../../App'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CreditCardOffRoundedIcon from '@mui/icons-material/CreditCardOffRounded'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'

const TRIAL_ENDING_SOON_HOURS = 72

const BillingWarning = () => {
  const navigate = useNavigate()
  const { tenantInfo } = useContext(GlobalContext)
  const [showBillingWarning, setShowBillingWarning] = useState(false)
  const [warningOff, setWarningOff] = useState(false)
  const { user } = useContext(UserContext)
  const location = useLocation()

  useEffect(() => {
    const isOnBillingPage = ['/billing', '/site-configuration/billing'].some(
      (path) => location.pathname === path,
    )
    if (!tenantInfo || warningOff) return
    // Manually-billed tenants (Enterprise+ invoiced outside Stripe) never
    // expire automatically — no expiry/renewal nagging applies to them.
    if (tenantInfo.manualBilling === true) {
      setShowBillingWarning(false)
      return
    }
    const now = moment()
    const subscriptionExpiry = tenantInfo?.stripeSubscriptionExpiry
      ? moment(tenantInfo.stripeSubscriptionExpiry)
      : null
    const graceUntil = tenantInfo?.stripePaymentGraceUntil
      ? moment(tenantInfo.stripePaymentGraceUntil)
      : null
    const stripeSubscriptionStatus = String(
      tenantInfo?.stripeSubscriptionStatus || '',
    ).toLowerCase()
    const hasSubscription =
      !!tenantInfo?.stripeSubscriptionId &&
      ['active', 'trialing', 'past_due', 'unpaid'].includes(
        stripeSubscriptionStatus,
      )
    const isDelinquent = ['past_due', 'unpaid'].includes(
      stripeSubscriptionStatus,
    )
    const graceExpired = !graceUntil || graceUntil.isSameOrBefore(now)
    // Server-computed: renewal window or explicit grace, whichever is later
    const effectiveGrace = tenantInfo?.effectiveGraceUntil
      ? moment(tenantInfo.effectiveGraceUntil)
      : null
    const trialEnd = tenantInfo?.trialEnd ? moment(tenantInfo.trialEnd) : null
    const trialEndingSoon =
      tenantInfo?.inInitialTrialPeriod &&
      !hasSubscription &&
      trialEnd?.isAfter(now) &&
      trialEnd?.isSameOrBefore(
        moment(now).add(TRIAL_ENDING_SOON_HOURS, 'hours'),
      )

    if (isOnBillingPage) {
      setShowBillingWarning(false)
    } else if (
      isDelinquent &&
      graceExpired &&
      subscriptionExpiry?.isAfter(now)
    ) {
      setShowBillingWarning({
        title: 'Billing attention required',
        message: `Payment for your subscription is overdue. Please update your billing details before access ends ${subscriptionExpiry.fromNow()}.`,
        icon: 'billing',
      })
    } else if (
      subscriptionExpiry?.isSameOrBefore(now) &&
      effectiveGrace?.isAfter(now)
    ) {
      setShowBillingWarning({
        title: isDelinquent ? 'Payment failed' : 'Renewal payment pending',
        message: isDelinquent
          ? `We couldn't take your latest subscription payment. Please update your billing details — access continues until ${effectiveGrace.format('D MMM YYYY')}.`
          : `We're waiting for your renewal payment to clear. Access continues until ${effectiveGrace.format('D MMM YYYY')} while it does.`,
        icon: 'billing',
      })
    } else if (
      tenantInfo?.stripeSubscriptionExpiry &&
      subscriptionExpiry?.isSameOrBefore(now)
    ) {
      setShowBillingWarning({
        title: 'Billing attention required',
        message: `Your ${
          tenantInfo?.inInitialTrialPeriod ? 'free trial' : 'subscription'
        } has expired`,
        icon: 'billing',
      })
    } else if (trialEndingSoon) {
      setShowBillingWarning({
        title: 'Trial ending soon',
        message: `Your free trial will expire ${trialEnd.fromNow()}. Choose a plan to keep AmbuNet running without interruption.`,
        icon: 'trial',
      })
    } else if (tenantInfo?.inInitialTrialPeriod) {
      setShowBillingWarning(false)
    } else if (
      subscriptionExpiry?.isBefore(moment().add(3, 'days')) &&
      !tenantInfo?.stripeSubscriptionId
    ) {
      setShowBillingWarning({
        title: 'Billing attention required',
        message: `Your subscription will end ${subscriptionExpiry.fromNow()}.`,
        icon: 'billing',
      })
    } else {
      setShowBillingWarning(false)
    }
  }, [tenantInfo, location, warningOff])

  if (warningOff || !showBillingWarning || !user.security?.isSiteAdmin) {
    return null
  }

  const Icon =
    showBillingWarning.icon === 'trial'
      ? AccessTimeRoundedIcon
      : CreditCardOffRoundedIcon

  return (
    <Box
      role="alert"
      sx={{
        mx: { xs: 1, md: 2 },
        mt: 1.5,
        mb: 1,
        px: { xs: 1.5, md: 2 },
        py: { xs: 1.25, md: 1.5 },
        borderRadius: 2,
        border: '1px solid rgba(230, 126, 34, 0.28)',
        background:
          'linear-gradient(135deg, rgba(255, 247, 237, 0.98) 0%, rgba(255, 255, 255, 0.96) 48%, rgba(232, 244, 248, 0.94) 100%)',
        boxShadow:
          '0 18px 45px rgba(36, 47, 64, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.7)',
        color: '#243040',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          width: 5,
          background: 'linear-gradient(180deg, #F59E0B 0%, #0E7490 100%)',
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        gap={{ xs: 1.25, md: 2 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        sx={{ pl: { xs: 1, md: 1.25 } }}
      >
        <Stack direction="row" gap={1.5} alignItems="center" minWidth={0}>
          <Box
            sx={{
              width: 42,
              height: 42,
              flex: '0 0 auto',
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              color: '#92400E',
              background:
                'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(14, 116, 144, 0.12))',
              border: '1px solid rgba(245, 158, 11, 0.22)',
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box minWidth={0}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                lineHeight: 1.2,
                color: '#111827',
              }}
            >
              {showBillingWarning.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 0.25,
                color: '#4B5563',
                lineHeight: 1.45,
              }}
            >
              {showBillingWarning.message}
            </Typography>
          </Box>
        </Stack>
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          justifyContent={{ xs: 'flex-end', md: 'center' }}
          flexShrink={0}
        >
          <Button
            onClick={() => navigate('/site-configuration/billing')}
            size="small"
            variant="contained"
            endIcon={<ArrowForwardRoundedIcon />}
            sx={{
              minHeight: 36,
              borderRadius: 1.5,
              px: 1.75,
              backgroundColor: '#111827',
              color: '#fff',
              boxShadow: '0 10px 24px rgba(17, 24, 39, 0.18)',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': {
                backgroundColor: '#0E7490',
                boxShadow: '0 12px 28px rgba(14, 116, 144, 0.24)',
              },
            }}
          >
            View billing
          </Button>
          <IconButton
            aria-label="Dismiss billing warning"
            color="inherit"
            size="small"
            onClick={() => setWarningOff(true)}
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              color: '#4B5563',
              border: '1px solid rgba(75, 85, 99, 0.16)',
              backgroundColor: 'rgba(255, 255, 255, 0.68)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                color: '#111827',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  )
}

export default BillingWarning
